import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import timeUtil from 'utils/TimeUtil';
import LogAPI from 'apis/LogAPI';
import { useTranscriptCoverage } from 'hooks/transcripts/queries';
import AssigneeCell from './AssigneeCell';
import ProgressCell from './ProgressCell';
import DashboardRowMenu from './DashboardRowMenu';
import OverflowTooltip, { WHITE_TOOLTIP_PROPS } from './OverflowTooltip';
import { useDebouncedValue } from 'hooks/utils/useDebouncedValue';
import { PAGE_SIZE, sortRows, filterRows, isAssignedTo, rowProgress, rowRecency, transcriptDisplayName } from './dashboardUtils';
import ColorConstants from 'constants/ColorConstants';

const BORDER = ColorConstants.GRAY_200;
const COL_BORDER = `1px solid ${ColorConstants.GRAY_200}`;
const CELL_PX = '14px';
const ASSIGNEES_SHOW_MAX = 3;
// Quiet period before a search counts as a logged interaction. Longer than the
// filter debounce so we record the user's settled query — one event per search
// — rather than every intermediate keystroke.
const SEARCH_TRACK_DELAY_MS = 600;

const COL_HEADER_SX = {
  fontWeight: 600,
  fontSize: 13,
  color: ColorConstants.GRAY_900,
  lineHeight: 1,
};

const CODE_COVERAGE_TOOLTIP = (
  <Box>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: ColorConstants.GRAY_900, mb: 0.5 }}>Code coverage</Typography>
    <Typography sx={{ fontSize: 13, color: ColorConstants.GRAY_600, lineHeight: 1.4 }}>
      Shows where in the transcript each team member has coded.
    </Typography>
  </Box>
);

function SortableHeader({ label, col, sortCol, sortDir, onSort, sx, tooltip }) {
  const active = sortCol === col;
  const labelText = (
    <Typography sx={{ ...COL_HEADER_SX, color: active ? ColorConstants.ACTIVE_BUTTON_BLUE : undefined }}>
      {label}
    </Typography>
  );
  return (
    <Box
      onClick={() => onSort(col)}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.75 }, ...sx }}
    >
      {tooltip ? (
        <Tooltip title={tooltip} placement="top" {...WHITE_TOOLTIP_PROPS}>
          {labelText}
        </Tooltip>
      ) : labelText}
      {active && (
        <Typography sx={{ fontSize: 10, color: ColorConstants.ACTIVE_BUTTON_BLUE, lineHeight: 1, mt: '1px' }}>
          {sortDir === 'asc' ? '▲' : '▼'}
        </Typography>
      )}
    </Box>
  );
}

// Fresh sorts on a column start in its most useful direction.
const DEFAULT_SORT_DIR = {
  name: 'asc',
  assigned: 'desc',
  progress: 'desc',
  updated: 'desc',
};

// Amplitude sort_type names per the dashboard pitch's Goal section
const SORT_TYPE_NAMES = {
  name: 'transcript',
  assigned: 'assigned_to',
  progress: 'progress',
  updated: 'last_updated',
};

function DashboardTable({ rows, currentUserId, people, hasEditPermission, projectID, onToggleAssign }) {
  const history = useHistory();
  const [sortCol, setSortCol] = useState('updated');
  const [sortDir, setSortDir] = useState('desc');
  // null | 'assigned_to_me' | 'unassigned' | 'not_started' (single-select)
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Filter on the debounced term so a fast typer doesn't re-filter (and
  // refetch the visible page's coverage bars) on every keystroke. The input
  // itself stays bound to searchQuery, so typing remains instant.
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const [page, setPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const logInteraction = (event, metadata) => {
    LogAPI.logEvent(event, { ...metadata, project_id: projectID }).catch(() => {});
  };

  // Log one search event per settled query. We debounce on the trimmed term so
  // a burst of keystrokes collapses to a single event, and only fire when the
  // settled term actually changes — refining "Ma" → "Mast" after a pause is a
  // new search, but re-running the same term (or clearing it) is not.
  const settledSearch = useDebouncedValue(searchQuery.trim(), SEARCH_TRACK_DELAY_MS);
  const lastTrackedSearch = useRef('');
  useEffect(() => {
    if (!settledSearch) {
      lastTrackedSearch.current = '';
      return;
    }
    if (settledSearch === lastTrackedSearch.current) return;
    lastTrackedSearch.current = settledSearch;
    logInteraction('search-transcript-list', {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledSearch]);

  const handleSort = (col) => {
    setPage(0);
    logInteraction('sort-transcript-list', { sort_type: SORT_TYPE_NAMES[col] });
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(DEFAULT_SORT_DIR[col]);
    }
  };

  const toggleExpanded = (transcriptId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(transcriptId)) {
        next.delete(transcriptId);
      } else {
        next.add(transcriptId);
      }
      return next;
    });
  };

  const sortedRows = sortRows(rows, sortCol, sortDir);
  const filteredRows = filterRows(sortedRows, { activeFilter, searchQuery: debouncedSearch }, currentUserId);

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);
  // The row set can shrink under us (deletes, refetches) — never strand the
  // user past the last page.
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const pagedRows = filteredRows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Coverage bars are fetched only for the visible page — the expensive island
  // merge never runs for off-screen transcripts.
  const { data: coverageById } = useTranscriptCoverage(projectID, pagedRows.map(r => r.id));

  const assignedToMe = rows.filter(row => isAssignedTo(row, currentUserId)).length;
  const unassigned = rows.filter(row => (row.assignees || []).length === 0).length;
  const notStarted = rows.filter(row => rowProgress(row) === 0).length;

  const sortProps = { sortCol, sortDir, onSort: handleSort };

  const FILTERS = [
    { key: null, label: 'All Transcripts', count: null },
    { key: 'assigned_to_me', label: 'Assigned to me', count: assignedToMe },
    { key: 'unassigned', label: 'Unassigned', count: unassigned },
    { key: 'not_started', label: 'Not started', count: notStarted },
  ];

  const selectFilter = (key) => {
    if (key && activeFilter !== key) logInteraction('filter-transcript-list', { filter_type: key });
    setActiveFilter(key);
    setPage(0);
  };

  return (
    <Box aria-label="transcripts dashboard">
      {/* Filter bar — single-select filters + name search */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        {FILTERS.map(({ key, label, count }) => {
          const active = activeFilter === key;
          return (
            <Box
              key={String(key)}
              component="button"
              aria-pressed={active}
              onClick={() => selectFilter(key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.5, py: 0.5, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                border: '1px solid', outline: 'none', borderRadius: '20px',
                borderColor: active ? ColorConstants.ACTIVE_BUTTON_BLUE : ColorConstants.GRAY_200,
                backgroundColor: active ? ColorConstants.SELECTED_BLUE_BG : ColorConstants.GRAY_50,
                color: active ? ColorConstants.SELECTED_BLUE_TEXT : ColorConstants.GRAY_500,
                '&:hover': { borderColor: ColorConstants.ACTIVE_BUTTON_BLUE, backgroundColor: active ? ColorConstants.SELECTED_BLUE_BG : ColorConstants.GRAY_100 },
              }}
            >
              {count !== null && (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: active ? ColorConstants.SELECTED_BLUE_TEXT : ColorConstants.GRAY_700 }}>
                  {count}<Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: ColorConstants.GRAY_400 }}>/{rows.length}</Typography>
                </Typography>
              )}
              <Typography sx={{ fontSize: 13, color: 'inherit' }}>{label}</Typography>
            </Box>
          );
        })}

        <input
          type="text"
          placeholder="Filter by transcript name"
          value={searchQuery}
          onChange={e => {
            // Tracking happens in the debounced effect above, not per keystroke.
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          style={{
            width: 220,
            height: 32,
            padding: '0 12px',
            fontSize: 13,
            border: `1px solid ${ColorConstants.GRAY_200}`,
            borderRadius: 6,
            outline: 'none',
            fontFamily: 'inherit',
            color: ColorConstants.GRAY_900,
            backgroundColor: ColorConstants.GRAY_50,
          }}
          onFocus={e => { e.target.style.borderColor = ColorConstants.ACTIVE_BUTTON_BLUE; e.target.style.backgroundColor = ColorConstants.WHITE; }}
          onBlur={e => { e.target.style.borderColor = ColorConstants.GRAY_200; e.target.style.backgroundColor = ColorConstants.GRAY_50; }}
        />
      </Box>

      {/* Table + pagination share the table's fit-content width, so the
          pagination's Next lands on the table's right edge no matter how wide
          the filter bar above gets. */}
      <Box sx={{ width: 'fit-content' }}>
      {/* No overflow clipping here — the react-bootstrap assign dropdown
          renders in-place and must be able to extend past the table edge. */}
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '6px', width: 'fit-content' }}>
        {/* Column headers */}
        <Box className="dashboardHeader" sx={{ display: 'flex', alignItems: 'center', backgroundColor: ColorConstants.GRAY_50, borderBottom: `1px solid ${BORDER}`, borderRadius: '5px 5px 0 0' }}>
            <SortableHeader label="Transcript" col="name" {...sortProps} sx={{ width: 220, flexShrink: 0, pl: '30px', pr: CELL_PX, py: 1.5, borderRight: COL_BORDER }} />
            <SortableHeader label="Assigned to" col="assigned" {...sortProps} sx={{ width: 220, px: CELL_PX, py: 1.5, borderRight: COL_BORDER }} />
            <SortableHeader label="Code coverage" col="progress" tooltip={CODE_COVERAGE_TOOLTIP} {...sortProps} sx={{ width: 160, px: CELL_PX, py: 1.5, borderRight: COL_BORDER }} />
            <SortableHeader label="Last updated" col="updated" {...sortProps} sx={{ width: 120, px: CELL_PX, py: 1.5, borderRight: COL_BORDER }} />
            <Box sx={{ width: 36, flexShrink: 0 }} />
          </Box>

          {filteredRows.length === 0 && (
            <Box sx={{ py: 8, px: 2, textAlign: 'center', width: '100%' }}>
              {debouncedSearch ? (
                <>
                  <Typography sx={{ fontSize: 22, color: ColorConstants.GRAY_400, mb: 1.5, fontWeight: 400, fontFamily: '"Libre Baskerville", Georgia, serif' }}>
                    We couldn't find any transcripts with that name.
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: ColorConstants.GRAY_400, fontFamily: '"Libre Baskerville", Georgia, serif' }}>
                    If you have a filter on, try clicking "All Transcripts" before searching again.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 16, color: ColorConstants.GRAY_400 }}>
                  No transcripts match this filter.
                </Typography>
              )}
            </Box>
          )}

          {pagedRows.map(row => {
            const assignees = row.assignees || [];
            const isExpanded = expandedRows.has(row.id);
            const visibleAssignees = isExpanded ? assignees : assignees.slice(0, ASSIGNEES_SHOW_MAX);
            const hiddenCount = assignees.length - ASSIGNEES_SHOW_MAX;

            return (
              <Box
                key={row.id}
                className="dashboardRow"
                sx={{
                  display: 'flex',
                  // stretch so each cell fills the row height and its right
                  // border runs the full height (continuous column dividers)
                  alignItems: 'stretch',
                  borderBottom: `1px solid ${BORDER}`,
                  '&:last-child': { borderBottom: 'none', borderRadius: '0 0 5px 5px' },
                  '&:hover': { backgroundColor: ColorConstants.GRAY_50 },
                }}
              >
                <Box sx={{ width: 220, flexShrink: 0, display: 'flex', alignItems: 'flex-start', overflow: 'hidden', pl: '30px', pr: CELL_PX, py: 1.5, borderRight: COL_BORDER }}>
                  <OverflowTooltip
                    className="dashboardTranscriptName"
                    component="button"
                    type="button"
                    onClick={() => history.push(`/transcripts/${row.id}`)}
                    sx={{
                      fontSize: 15,
                      cursor: 'pointer', color: ColorConstants.ACTIVE_BUTTON_BLUE,
                      // Real <button> for keyboard/AT access; strip native chrome
                      // so it still reads as a plain text link. Keep the default
                      // focus outline for keyboard users.
                      appearance: 'none', border: 'none', background: 'none', p: 0,
                      fontFamily: 'inherit', textAlign: 'left',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {transcriptDisplayName(row)}
                  </OverflowTooltip>
                </Box>

                <Box className="dashboardAssignees" sx={{ width: 220, px: CELL_PX, py: 1.5, borderRight: COL_BORDER }}>
                  <AssigneeCell
                    row={row}
                    people={people}
                    visibleAssignees={visibleAssignees}
                    hiddenCount={hiddenCount}
                    isExpanded={isExpanded}
                    onToggleExpanded={() => toggleExpanded(row.id)}
                    canAssign={hasEditPermission}
                    onToggleAssign={(person, assigned) => onToggleAssign(row, person, assigned)}
                  />
                </Box>

                <Box className="dashboardProgress" sx={{ width: 160, px: CELL_PX, py: 1.5, borderRight: COL_BORDER }}>
                  <ProgressCell
                    assignees={row.assignees}
                    coverage={coverageById?.[row.id]}
                    visibleAssignees={visibleAssignees}
                  />
                </Box>

                <Box sx={{ width: 120, px: CELL_PX, py: 1.5, borderRight: COL_BORDER }}>
                  <Typography className="dashboardLastUpdated" sx={{ fontSize: 13, color: ColorConstants.GRAY_400 }}>
                    {timeUtil.getDisplayTime(rowRecency(row))}
                  </Typography>
                </Box>

                <Box sx={{ width: 36, flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 1.5 }}>
                  <DashboardRowMenu
                    projectID={projectID}
                    transcriptId={row.id}
                    canCompare={(row.coder_count || 0) >= 2}
                    canDelete={hasEditPermission}
                  />
                </Box>
              </Box>
            );
          })}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box
            component="button"
            onClick={() => setPage(safePage - 1)}
            disabled={safePage === 0}
            sx={{
              px: 2.5, py: 1.25, fontSize: 14, fontFamily: 'inherit', cursor: safePage === 0 ? 'default' : 'pointer',
              border: `1px solid ${ColorConstants.GRAY_300}`, borderRadius: '6px', backgroundColor: ColorConstants.WHITE,
              color: safePage === 0 ? ColorConstants.GRAY_300 : ColorConstants.GRAY_900,
              '&:hover': { borderColor: safePage === 0 ? ColorConstants.GRAY_300 : ColorConstants.GRAY_400 },
            }}
          >
            Previous
          </Box>
          <Typography sx={{ fontSize: 13, color: ColorConstants.GRAY_400, alignSelf: 'center' }}>
            {safePage + 1} / {totalPages}
          </Typography>
          <Box
            component="button"
            onClick={() => setPage(safePage + 1)}
            disabled={safePage >= totalPages - 1}
            sx={{
              px: 2.5, py: 1.25, fontSize: 14, fontFamily: 'inherit', cursor: safePage >= totalPages - 1 ? 'default' : 'pointer',
              border: `1px solid ${ColorConstants.GRAY_300}`, borderRadius: '6px', backgroundColor: ColorConstants.WHITE,
              color: safePage >= totalPages - 1 ? ColorConstants.GRAY_300 : ColorConstants.GRAY_900,
              '&:hover': { borderColor: safePage >= totalPages - 1 ? ColorConstants.GRAY_300 : ColorConstants.GRAY_400 },
            }}
          >
            Next
          </Box>
        </Box>
      )}
      </Box>
    </Box>
  );
}

export default DashboardTable;
